import { ScrollView, View } from "react-native";

import { useAppTheme } from "@hooks/useAppTheme";
import { Button, Divider, Icon, Text } from "react-native-paper";

import { useActiveScenario } from "../hooks/useActiveScenario";
import { PersonaAvatar } from "./PersonaAvatar";
import { ScenarioMarkdown } from "./ScenarioMarkdown";
import { ScenarioSummary } from "./ScenarioSummary";

const SectionLabel = ({ children }: { children: string }) => {
	const theme = useAppTheme();
	return (
		<Text
			variant="labelSmall"
			style={{
				color: theme.colors.onSurfaceVariant,
				textTransform: "uppercase",
				letterSpacing: 1,
				marginBottom: 6,
			}}
		>
			{children}
		</Text>
	);
};

export const ScenarioIntro = ({ onStart }: { onStart: () => void }) => {
	const theme = useAppTheme();
	const { scenario: s } = useActiveScenario();

	return (
		<ScrollView
			style={{ flex: 1, backgroundColor: theme.colors.surface }}
			contentContainerStyle={{
				flexGrow: 1,
				alignItems: "center",
				justifyContent: "center",
				paddingHorizontal: 24,
				paddingVertical: 16,
			}}
		>
			<Text
				variant="titleLarge"
				style={{
					textAlign: "center",
					color: theme.colors.onSurface,
					marginBottom: 12,
				}}
			>
				{s.screenTitle}
			</Text>
			<View
				style={{
					width: "100%",
					maxWidth: 720,
					backgroundColor: theme.colors.elevation.level1,
					borderRadius: theme.roundness * 2,
					borderWidth: 1,
					borderColor: theme.colors.outlineVariant,
					padding: 16,
				}}
			>
				<SectionLabel>Scenario</SectionLabel>
				<ScenarioSummary
					paragraphs={s.scenarioSummary}
					media={s.scenarioMedia}
				/>
				<Divider style={{ marginVertical: 12 }} />
				<SectionLabel>OBJECTIVES</SectionLabel>
				<View style={{ gap: 6 }}>
					{s.learningObjectives.map((goal) => (
						<View
							key={goal}
							style={{
								flexDirection: "row",
								alignItems: "flex-start",
								gap: 8,
							}}
						>
							<View style={{ marginTop: 2 }}>
								<Icon
									source="check-circle-outline"
									size={16}
									color={theme.colors.primary}
								/>
							</View>
							<View style={{ flex: 1 }}>
								<ScenarioMarkdown text={goal} />
							</View>
						</View>
					))}
				</View>
				<Divider style={{ marginVertical: 12 }} />
				<View style={{ gap: 8 }}>
					<View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
						<PersonaAvatar size={40} />
						<View style={{ flex: 1 }}>
							<Text
								variant="titleSmall"
								style={{ color: theme.colors.onSurface }}
							>
								{s.persona.name}
							</Text>
							<Text
								variant="bodySmall"
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{s.persona.role}
							</Text>
						</View>
					</View>
					{s.persona.description && (
						<ScenarioMarkdown text={s.persona.description} />
					)}
				</View>
			</View>
			<Button
				mode="contained"
				icon="message-text"
				buttonColor={theme.colors.primary}
				textColor={theme.colors.onPrimary}
				contentStyle={{ height: 48, paddingHorizontal: 20 }}
				style={{ marginTop: 16 }}
				accessibilityLabel="Start conversation"
				onPress={onStart}
			>
				Start conversation
			</Button>
		</ScrollView>
	);
};
